import React, { useMemo, useState } from 'react'
import { MdAcUnit, MdClose } from 'react-icons/md'
import type { SchedulePeriod } from '@/utils/deliveryOverlay'
import { toLocalDateString } from '@/utils/deliveryDays'
import '@/styles/components/schedule-change-banner.css'

const DISMISSED_KEY = 'schedule-banner-dismissed'

/** "21 and 28 December", or "28 December and 4 January" across a month end. */
const weeksLabel = (mondays: string[]): string => {
  const parts = mondays.map(key => {
    const [y, m, d] = key.split('-').map(Number)
    const date = new Date(y, m - 1, d)
    return { day: String(date.getDate()), month: date.toLocaleDateString('en-GB', { month: 'long' }) }
  })
  const sameMonth = parts.every(p => p.month === parts[0].month)
  const items = parts.map((p, i) => (sameMonth && i < parts.length - 1 ? p.day : `${p.day} ${p.month}`))
  return items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}` : items[0]
}

const readDismissed = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? '[]') as string[]
  } catch {
    return []
  }
}

/**
 * DF-478: tells a customer, when they land on My Deliveries, that their
 * delivery calendar has changed for a special schedule (Christmas). Shown
 * until the period is over or they close it; closing is remembered in this
 * browser, per period.
 */
const ScheduleChangeBanner: React.FC<{ periods: SchedulePeriod[] }> = ({ periods }) => {
  const [dismissed, setDismissed] = useState<string[]>(readDismissed)

  const today = toLocalDateString(new Date())
  const period = useMemo(
    () => periods.find(p => p.to >= today && !dismissed.includes(p.from)),
    [periods, today, dismissed]
  )

  if (!period) return null

  const close = () => {
    const next = [...dismissed, period.from]
    setDismissed(next)
    try {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(next))
    } catch {
      // Not remembered (private browsing); it just shows again next visit.
    }
  }

  const weeks = `${period.weeks.length > 1 ? 'weeks' : 'week'} commencing ${weeksLabel(period.weeks)}`

  return (
    <div className={`schedule-banner${period.christmas ? ' is-christmas' : ''}`} role="status">
      <MdAcUnit className="schedule-banner-icon" aria-hidden="true" />
      <div className="schedule-banner-content">
        <h4 className="schedule-banner-title">
          {period.christmas ? 'Your Christmas deliveries have been updated!' : 'Your deliveries have been updated!'}
        </h4>
        <p className="schedule-banner-message">
          {period.christmas
            ? `We've updated your delivery calendar to reflect our Christmas schedule. Please check your deliveries for the ${weeks}.`
            : `We've updated your delivery calendar to reflect a change to our delivery schedule. Please check your deliveries for the ${weeks}.`}
        </p>
      </div>
      <button type="button" className="schedule-banner-close" onClick={close} aria-label="Close this message">
        <MdClose aria-hidden="true" />
      </button>
    </div>
  )
}

export default ScheduleChangeBanner
